import { Component, Prop, Ref } from 'vue-property-decorator'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import ConfigurationLocalAccountingBillableRateModalConceptsGridBasicForm from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-modal-concepts-grid-basic-form/configuration-local-accounting-billable-rate-modal-concepts-grid-basic-form.vue'
import { ConfigurationLocalAccountingRateConceptsGridState, initialLocalAccountingRateConceptsGridState } from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-modal-concepts-grid/configuration-local-accounting-billable-rate-modal-concepts-grid.model'
import { BModal } from 'bootstrap-vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import { BillableRateBillableConceptPatchInBatchRequest, BillableRateBillableConceptResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { billableRateBillableConceptService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.service'
import { BillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.dto'

@Component({
    components: {
        VtGrid,
        ConfigurationLocalAccountingBillableRateModalConceptsGridBasicForm
    }
})
export default class ConfigurationLocalAccountingBillableRateModalConceptsGrid extends BaseCrudServerSideComponent<BillableRateBillableConceptResponse> implements BaseCrud {
    @Ref() readonly ratesModalConceptsBasicFormWrapper!: BModal
    @Prop() readonly rate!: BillableRateResponse

    state: ConfigurationLocalAccountingRateConceptsGridState

    constructor() {
        super()
        this.state = initialLocalAccountingRateConceptsGridState(this.rate.id)
    }

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    private async _deleteLocalAccountingBillableRates(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///
    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteLocalAccountingBillableRates(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///
    onUpdate(localAccountingBillableRate: BillableRateBillableConceptResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (localAccountingBillableRate) {
            this.showModal(this.$tc('configuration.actions.update'), localAccountingBillableRate, this.ratesModalConceptsBasicFormWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(localAccountingBillableRate => localAccountingBillableRate.id as number)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: BillableRateBillableConceptResponse[]): Promise<void> {
        const filteredRowActiveState = selectionBatch?.filter((row: BillableRateBillableConceptResponse) => row.active !== option)
        const updatedFilteredRowActiveState: BillableRateBillableConceptPatchInBatchRequest[] = filteredRowActiveState?.map((row: BillableRateBillableConceptResponse) => {
            return {
                id: row.id!,
                active: option
            }
        })
        if (updatedFilteredRowActiveState?.length) {
            const response = await this.$_toCallSecure(billableRateBillableConceptService.patch(
                this.state.laboratoryId,
                updatedFilteredRowActiveState
            ))
            if (response) {
                if (selectionBatch.length > 1) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
                } else {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
                }
                this.reloadData()
            }
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.ratesModalConceptsBasicFormWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: !this.canUpdate ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: !this.canDelete ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.onActivate()
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.onDeactivate()
            }
        ]
    }
}
